import type { HeaderGroup } from '@tanstack/react-table';
import * as React from 'react';

import SimpleHeaderRowItem from './SimpleHeaderRowItem';
import styles from './styles.module.scss';
import type { CalculatedHeaderData } from './types';

interface OwnProps<T> {
	gridTemplateColumns: string;
	headerRef: React.Ref<Nullable<HTMLDivElement>>;
	onColumnDrag: (header: string) => (e: React.MouseEvent<Element, MouseEvent>) => void;
	setHeaderReference: (headerId: string) => (_ref: HTMLDivElement) => void;
	headerData: CalculatedHeaderData<T>;
}

type Props<T> = OwnProps<T>;

function SimpleHeaderRow<T>(props: Props<T>) {
	const {
		gridTemplateColumns,
		headerRef,
		onColumnDrag,
		setHeaderReference,
		headerData,
	} = props;

	const { headerGroups, leafColumns } = headerData;

	const leafIds = React.useMemo(() => leafColumns.map((col) => col.id), [leafColumns]);
	const leafIdIndex = React.useMemo(() => new Map(leafIds.map((id, idx) => [id, idx])), [leafIds]);
	const leafCount = React.useMemo(() => leafIds.length, [leafIds]);

	const renderHeader = React.useCallback((header: HeaderGroup<T>['headers'][number]) => {
		return (
			<SimpleHeaderRowItem<T>
				header={header}
				leafCount={leafCount}
				leafIdIndex={leafIdIndex}
				onColumnDrag={onColumnDrag}
				setHeaderReference={setHeaderReference}
			/>
		);
	}, [leafCount, leafIdIndex, onColumnDrag, setHeaderReference]);

	const renderHeaderGroup = React.useCallback((headerGroup: HeaderGroup<T>, index: number) => {
		return (
			<div
				className={styles['simple-table__header-row']}
				key={headerGroup.id}
				ref={index === 0 ? headerRef : undefined}
				role="row"
				style={{ gridTemplateColumns }}
			>
				{headerGroup.headers.map(renderHeader)}
			</div>
		);
	}, [gridTemplateColumns, renderHeader, headerRef]);

	return (
		<>
			{headerGroups.map(renderHeaderGroup)}
		</>
	);
}

export default React.memo(SimpleHeaderRow) as typeof SimpleHeaderRow;
